import KC_Tabs, {
  type KC_TabItem
} from "../../../designsystem/KC_Tabs/KC_Tabs";
import KC_UIText from "../../../designsystem/typography/KC_UIText/KC_UIText";
import { KC_Weights } from "../../../designsystem/ds_commons";
import {
  DEFAULT_PLAN_OPTION,
  DURATION_BASED_DISCOUNT
} from "../../../utils/constants";
import SubscriptionPlanDTO, {
  SubscriptionPlanType
} from "@tootsweet/model/datatransfer/SubscriptionPlanDTO";
import React, { useEffect, useMemo, useState } from "react";
import {
  setCurrency,
  useCurrencySelector,
  useSubscriptionPlansSelector
} from "../../../redux/actions/app";
import { useAccountToDisplaySelector } from "../../../redux/actions/user";
import { tt } from "../../../utils/i18n/i18n";
import PlanOption from "./PlanOption";
import { DEFAULT_CURRENCY } from "@tootsweet/model/enums/Currencies";
import CurrencySelector from "../../CurrencySelector/CurrencySelector";
import { useDispatch } from "react-redux";
import PlanFamilyFilter, {
  PlanFilterMode
} from "../../PlanFamilyFilter/PlanFamilyFilter";

export default function PlanOptions(props: Props) {
  const dispatch = useDispatch();

  const subscriptionPlans = useSubscriptionPlansSelector();
  const { accountToDisplay } = useAccountToDisplaySelector();
  const reduxCurrency = useCurrencySelector();

  const initialPlanIndex = DURATION_OPTIONS.findIndex((option: KC_TabItem) => {
    return option.value === DEFAULT_PLAN_OPTION || false;
  });

  const seedCurrency = useMemo(() => {
    return (
      accountToDisplay?.businessUser?.currency ||
      reduxCurrency ||
      DEFAULT_CURRENCY
    );
  }, [accountToDisplay?.businessUser?.currency, reduxCurrency]);

  const [planDuration, setPlanDuration] = useState(DEFAULT_PLAN_OPTION);
  const [selectedCurrency, setSelectedCurrency] = useState(seedCurrency);

  const initialFilterMode = useMemo(() => {
    if (accountToDisplay?.saasPlanId && subscriptionPlans) {
      const currentPlan = subscriptionPlans.find(
        (plan) => plan._id === accountToDisplay.saasPlanId
      );
      if (currentPlan?.saasFeatures?.lty) {
        return "loyalty" as PlanFilterMode;
      }
    }
    return "ambassador" as PlanFilterMode;
  }, [accountToDisplay?.saasPlanId, subscriptionPlans]);

  const [filterMode, setFilterMode] =
    useState<PlanFilterMode>(initialFilterMode);

  const handleCurrencyChange = (currency: string) => {
    setSelectedCurrency(currency);
    dispatch(setCurrency(currency));
  };

  const discountText = useMemo(() => {
    const percentage = DURATION_BASED_DISCOUNT * 100;
    if (planDuration === DEFAULT_PLAN_OPTION) {
      return tt("plan_discount_saved", { percentage });
    }
    return tt("plan_discount_yearly_save", { percentage });
  }, [planDuration]);

  const useSmallerFontsForPrices = useMemo(() => {
    let res = false;
    subscriptionPlans?.forEach((plan) => {
      /**
       * Ignore the free plan, because it's price is 0
       * and will always be lesser than the threshold.
       */
      if (!plan.price) {
        return;
      }

      if (plan.price > FONT_SIZE_THRESHOLD) {
        res = true;
      }
    });
    return res;
  }, [subscriptionPlans]);

  const handlePlanClick = (plan: SubscriptionPlanDTO) => {
    props.onPlanClick?.(plan, planDuration);
  };

  useEffect(() => {
    setSelectedCurrency(seedCurrency);
  }, [seedCurrency]);

  useEffect(() => {
    setFilterMode(initialFilterMode);
  }, [initialFilterMode]);

  return (
    <>
      <div className="mt-4">
        <PlanFamilyFilter
          filterMode={filterMode}
          onFilterChange={setFilterMode}
        />
      </div>
      <div className="text-center position-relative w-100 d-flex flex-column">
        <div className="d-flex flex-column flex-sm-row align-items-center justify-content-center">
          <div className="flex-grow-1 d-flex justify-content-center justify-content-sm-end mb-2 mb-sm-0">
            <KC_Tabs
              id="plan-option"
              items={DURATION_OPTIONS}
              onSelect={setPlanDuration}
              initialSelectedIndex={initialPlanIndex}
            />
          </div>
          <div className="flex-grow-1 d-flex justify-content-center justify-content-sm-start">
            <div className="price-reduction-tag">
              <KC_UIText weight={KC_Weights.md} text={discountText} />
            </div>
          </div>
        </div>
        <div className="currency-selector">
          <div className="currency-selector-inner px-4">
            <KC_UIText weight="bold" text={tt("currency")} className="mr-2" />
            <div className="currency-selector-container">
              <CurrencySelector
                value={selectedCurrency}
                onChange={handleCurrencyChange}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="my-4 px-4 plans-grid-container">
        <div className="plans-grid d-flex flex-column flex-md-row flex-nowrap justify-content-center">
          {subscriptionPlans?.map(
            (plan: SubscriptionPlanDTO, index: number) => {
              if (plan.hidden) {
                return null;
              }

              const isEnterprise =
                plan.type === SubscriptionPlanType.ENTERPRISE;

              if (isEnterprise && props.hideEnterprise) {
                return null;
              }

              const isFree = plan.type === SubscriptionPlanType.FREE;

              if (isFree && props.hideFree) {
                return null;
              }

              // Filter based on ambassador/loyalty mode
              if (filterMode === "ambassador") {
                // Hide plans that have saasFeatures.lty unless they have saasFeatures.ip
                if (plan.saasFeatures?.lty && !plan.saasFeatures?.ip) {
                  return null;
                }
              } else if (filterMode === "loyalty") {
                // Show only plans that have saasFeatures.lty OR saasFeatures.ip
                if (!plan.saasFeatures?.lty && !plan.saasFeatures?.ip) {
                  return null;
                }
              }

              // When signed-in, show only one free plan per filter mode
              if (isFree) {
                // Check if this free plan matches the current filter mode
                const planMatchesFilterMode =
                  (filterMode === "loyalty" && plan.saasFeatures?.lty) ||
                  (filterMode === "ambassador" && !plan.saasFeatures?.lty);

                if (!planMatchesFilterMode) {
                  return null;
                }

                // If user has a plan, show the free plan that matches the filter mode
                // (not necessarily their current plan)
                if (
                  !!accountToDisplay?.token &&
                  !!accountToDisplay.saasPlanId
                ) {
                  // Allow showing the free plan that matches the current filter mode
                  // Skip this check as we've already filtered by mode above
                }

                // If user doesn't have a plan, show only the first free plan
                if (
                  !!accountToDisplay?.token &&
                  !accountToDisplay.saasPlanId &&
                  index > 0
                ) {
                  return null;
                }
              }

              const isSelected =
                (!!accountToDisplay &&
                  !accountToDisplay?.saasPlanId &&
                  !!plan?.isDefault) ||
                (!!props.showCurrentPlan &&
                  plan._id === accountToDisplay?.saasPlanId);

              return (
                <PlanOption
                  key={plan._id}
                  plan={plan}
                  duration={planDuration}
                  currency={selectedCurrency}
                  isSelected={isSelected}
                  displayButtonOnFreePlan={true}
                  freePlanIsPink={props.freePlanIsPink}
                  useSmallerFontsForPrices={useSmallerFontsForPrices}
                  hasKCTheme={props.hasKCTheme}
                  accountToDisplay={accountToDisplay}
                  subscriptionPlans={subscriptionPlans}
                  onClick={handlePlanClick}
                  hideFree={props.hideFree}
                />
              );
            }
          )}
        </div>
      </div>
    </>
  );
}

const DURATION_OPTIONS: KC_TabItem[] = [
  { name: tt("order_summary_monthly"), value: "monthly" },
  { name: tt("order_summary_annual"), value: "yearly" }
];

const FONT_SIZE_THRESHOLD = 1000 * 100;

interface Props {
  freePlanIsPink?: boolean;
  hasKCTheme?: boolean;
  showCurrentPlan?: boolean;
  hideEnterprise?: boolean;
  hideFree?: boolean;
  onPlanClick?: (plan: SubscriptionPlanDTO, commitment: string) => void;
}
